// janus-engine/minigames/lockpick.ts — Lane L5 S2/S3: the lockpicking minigame's math and decisions in the browser.
//
// TS twins of includes/functions/inventory/lockpick_sweet_spot_inc.php (the arc / wedge geometry, the difficulty ladders, the
// caster-aware zone / feel-halo / struggle helpers — parity: tools/test/janus-l5-lockpick.test.mjs against the S0 oracle's
// helper matrix) plus what lockpick_init_inc.php / lockpick_result_inc.php / lockpick_break_inc.php used to decide on the
// server from compiled rows: the tools gate, the sweet-spot first mint, and WHICH Lockpicking Tools row a break or a failed pick
// spends. That last one is the proximity-row rule (docs/JANUS_CLIENT_TRUTH_STATUS.md "Nearby tools"): the browser selects the
// exact row — carried first (bags at any depth), then floor rows in reach by the v3 frontend geometry — and the server only
// proves holdership (the possess chain, or a floor row of the same encounter). No PHP geometry.
import {
  asRow, childrenOf, inventoryIdOf, isUuid, LOCKPICK_TOOLS_ITEM_ID, lockIsLocked, qtyOf, statusEntries, statusItemOf,
  templateIdOf, upper, walkRows, LOCKPICKING_STATUS_ID, type Row,
} from './rows.js';
import { layerListEntryUuid } from '../actions/spell-components.js';

// ---- constants (lockpick_sweet_spot_inc.php) ------------------------------------------------------------------------------
export const KNIFE_DEAD_START = 150;
export const KNIFE_DEAD_END = 210;
export const ARC_MIN = KNIFE_DEAD_END;
export const ARC_MAX = KNIFE_DEAD_START + 360;
export const DIFFICULTY_LADDER = [2, 4, 6, 8, 10, 12] as const;
export const PARTIAL_TURN_MAX = 0.78;
export const PCT_MIN = -90;
export const PCT_MAX = 1000;

const round2 = (n: number): number => Math.round(n * 100) / 100;
const round3 = (n: number): number => Math.round(n * 1000) / 1000;
/** PHP fmod keeps the sign of the dividend; so does JS %. */
export function extendedToClock(ext: number): number { let d = ext % 360; if (d < 0) d += 360; return d; }
export function clockToExtended(clock: number): number { let c = clock % 360; if (c < 0) c += 360; return c < ARC_MIN ? c + 360 : c; }
export function angularDistanceClock(a: number, b: number): number { const d = Math.abs(a - b); return Math.min(d, 360 - d); }
export function clampClockOutsideWedge(clock: number): number {
  let c = extendedToClock(clock);
  if (c > KNIFE_DEAD_START && c < KNIFE_DEAD_END) c = angularDistanceClock(c, KNIFE_DEAD_START) <= angularDistanceClock(c, KNIFE_DEAD_END) ? KNIFE_DEAD_START : KNIFE_DEAD_END;
  return c;
}
export function intervalCrossesDeadZone(startExt: number, endExt: number): boolean {
  if (startExt >= endExt) return true;
  for (let i = 0; i <= 28; i++) {
    const c = extendedToClock(startExt + (endExt - startExt) * (i / 28));
    if (c > KNIFE_DEAD_START && c < KNIFE_DEAD_END) return true;
  }
  return false;
}
/** lockpick_clamp_angle_to_arc: the submitted angle, snapped out of the wedge, on the ruler, 2 decimals. */
export function clampAngleToArc(deg: number): number {
  const ext = clockToExtended(clampClockOutsideWedge(extendedToClock(deg)));
  return Math.max(ARC_MIN, Math.min(ARC_MAX, round2(ext)));
}

// ---- difficulty ladders ----------------------------------------------------------------------------------------------------
export function baseSweetSpotWidth(dice: number): number {
  const d = Math.max(1, dice);
  return d <= 2 ? 80 : d <= 4 ? 55 : d <= 6 ? 35 : d <= 8 ? 20 : d <= 10 ? 12 : 7;
}
/** PHP round() is half-away-from-zero; dice are positive, so Math.round matches. */
export const lockTier = (dice: number): number => Math.max(1, Math.min(6, Math.round(dice / 2)));
export const skillEdge = (dice: number, tier: number): number => Math.max(0, tier) - lockTier(dice);
export function feelFalloffForTier(dice: number, tier: number): number {
  const e = skillEdge(dice, tier);
  return e <= -5 ? 8 : e === -4 ? 14 : e === -3 ? 24 : e === -2 ? 40 : e === -1 ? 65 : e === 0 ? 95 : e === 1 ? 135 : e === 2 ? 175 : 210;
}
export function struggleSecondsForTier(dice: number, tier: number): number {
  const e = skillEdge(dice, tier);
  return e <= -5 ? 0.30 : e === -4 ? 0.45 : e === -3 ? 0.70 : e === -2 ? 1.00 : e === -1 ? 1.45 : e === 0 ? 2.00 : e === 1 ? 2.60 : e === 2 ? 3.10 : 3.60;
}
/** lockpick_resolve_dice_count_for_zone: `lockpick_difficulty`, else the legacy `dice`, snapped to the ladder; default 6 (Major). */
export function resolveDiceCount(difficulty: unknown, legacyDice?: unknown): number {
  const pick = (v: unknown): number | null => (v === null || v === undefined || v === '' || typeof v === 'object' || !Number.isFinite(Number(v))) ? null : Math.trunc(Number(v));
  const d = pick(difficulty) ?? pick(legacyDice);
  if (d === null) return 6;
  if ((DIFFICULTY_LADDER as readonly number[]).includes(d)) return d;
  let nearest: number = DIFFICULTY_LADDER[0];
  for (const rung of DIFFICULTY_LADDER) if (Math.abs(rung - d) < Math.abs(nearest - d)) nearest = rung;
  return nearest;
}

// ---- the caster ------------------------------------------------------------------------------------------------------------
export interface LockpickCasterStats { tier: number; tierBonus: number; zonePct: number; durabilityPct: number }
const nonNeg = (v: unknown): number => (v === null || v === undefined || v === '' || typeof v === 'object' || !Number.isFinite(Number(v))) ? 0 : Math.max(0, Math.trunc(Number(v)));
const pct = (v: unknown): number => {
  if (v === null || v === undefined || v === '' || typeof v === 'object') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(PCT_MIN, Math.min(PCT_MAX, n)) : 0;
};
/** The four layers the PHP helpers read off the caster (layer_value), from a reader over the compiled row. */
export function casterStats(read: (key: string) => unknown): LockpickCasterStats {
  return { tier: nonNeg(read('lockpicking_mastery')), tierBonus: Math.min(6, nonNeg(read('lockpick_effective_skill_tier_bonus'))),
    zonePct: pct(read('lockpick_sweet_spot_changed_by_percentage')), durabilityPct: pct(read('lockpick_pick_durability_changed_by_percentage')) };
}
export const effectiveTier = (s: LockpickCasterStats): number => s.tier + s.tierBonus;
export const skillBonusDegrees = (s: LockpickCasterStats): number => s.tier * 5;
export const feelFalloffForCaster = (dice: number, s: LockpickCasterStats): number => feelFalloffForTier(dice, effectiveTier(s));
export const struggleSecondsForCaster = (dice: number, s: LockpickCasterStats): number =>
  round3(struggleSecondsForTier(dice, effectiveTier(s)) * (1 + s.durabilityPct / 100));
export function effectiveZoneForCaster(start: number, end: number, s: LockpickCasterStats): [number, number] {
  const bonus = skillBonusDegrees(s) + ((end - start) / 2) * (s.zonePct / 100);
  const effStart = Math.max(ARC_MIN, start - bonus), effEnd = Math.min(ARC_MAX, end + bonus);
  if (effStart >= effEnd) return [start, end];
  if (intervalCrossesDeadZone(effStart, effEnd)) return [Math.max(ARC_MIN, start), Math.min(ARC_MAX, end)];
  return [effStart, effEnd];
}

// ---- the lock's sweet spot -------------------------------------------------------------------------------------------------
/** lockpick_random_sweet_spot_range with an injected rand() ∈ [0,1). */
export function randomSweetSpot(widthDegrees: number, rand: () => number): [number, number] {
  const width = Math.max(1, Math.min(widthDegrees, ARC_MAX - ARC_MIN));
  const half = width / 2, minC = ARC_MIN + half, maxC = ARC_MAX - half;
  for (let attempt = 0; attempt < 140; attempt++) {
    const c = minC + rand() * (maxC - minC);
    const s = round2(c - half), e = round2(c + half);
    if (!intervalCrossesDeadZone(s, e)) return [s, e];
  }
  let s = ARC_MIN, e = round2(s + width);
  if (e > ARC_MAX) { e = ARC_MAX; s = round2(e - width); }
  return [s, e];
}
/**
 * lockpick_ensure_sweet_spot_on_inventory: the persisted pair when usable (an old-arc pair wholly below the floor is lifted a
 * whole turn — a pure re-label — and re-persisted), else a fresh mint. `persist` = what the commit must write (null when the
 * stored pair is used unchanged); the server writes it under a NULL/unchanged CAS — the first writer wins.
 */
export function ensureSweetSpot(stored: [unknown, unknown], dice: number, rand: () => number): { zone: [number, number]; persist: [number, number] | null; minted: boolean } {
  const num = (v: unknown): number | null => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))) ? null : Number(v);
  let s = num(stored[0]), e = num(stored[1]);
  if (s !== null && e !== null) {
    let lifted = false;
    if (s < ARC_MIN && e < ARC_MIN) { s += 360; e += 360; lifted = true; }
    if (s < e && s >= ARC_MIN && e <= ARC_MAX && !intervalCrossesDeadZone(s, e)) return { zone: [s, e], persist: lifted ? [round2(s), round2(e)] : null, minted: false };
  }
  const zone = randomSweetSpot(baseSweetSpotWidth(dice), rand);
  return { zone, persist: zone, minted: true };
}

// ---- tools: the gate, the pick supply, the pick a break spends ---------------------------------------------------------------
export interface ToolReaders {
  /** A catalog template id → its item_type ('' unknown). */
  itemType(row: Row): string;
  /** Frontend reach (ft3d-combat-distance-v3) from the actor to a FLOOR row; null = unknown geometry (never permits). */
  reachable(floorRowId: string): boolean | null;
}
export interface PickRow { inventoryId: string; qty: number; source: 'carried' | 'floor'; floorRootId: string | null; holders: string[] }
/** Rows never walked into: a digsite's buried contents are hidden information (lockpick_collect_pick_rows walked them — an L5
 * S0 finding: a buried pick counted as an encounter pick). */
const BURIED_HOLDER_TYPES = new Set(['digsite']);

function rowsOfTemplate(roots: unknown, templateId: string, read: ToolReaders): { row: Row; holders: string[] }[] {
  const out: { row: Row; holders: string[] }[] = [];
  const want = upper(templateId);
  const walk = (rows: unknown, holders: string[], depth: number): void => {
    if (!Array.isArray(rows) || depth > 8) return;
    for (const raw of rows) {
      const row = asRow(raw);
      if (!row) continue;
      const id = inventoryIdOf(row);
      if (templateIdOf(row) === want && qtyOf(row) > 0 && isUuid(id)) out.push({ row, holders });
      if (BURIED_HOLDER_TYPES.has(read.itemType(row))) continue;
      const kids = childrenOf(row);
      if (kids.length) walk(kids, id ? [...holders, id] : holders, depth + 1);
    }
  };
  walk(roots, [], 0);
  return out;
}
/**
 * Every usable stack of `templateId`, in spend order: the actor's carried tree first (direct rows before bag contents), then
 * floor rows (a floor row itself, or rows inside a floor container) whose FLOOR ROOT is in reach. `outOfReach` counts floor
 * stacks that exist but are too far — the gate's `lockpicking_tools_out_of_reach` reason.
 */
export function usableStacks(actorRow: Row, encounter: Row, templateId: string, read: ToolReaders): { rows: PickRow[]; outOfReach: number } {
  const carried = rowsOfTemplate(actorRow.inventory, templateId, read)
    .sort((a, b) => a.holders.length - b.holders.length)
    .map(({ row, holders }) => ({ inventoryId: inventoryIdOf(row), qty: qtyOf(row), source: 'carried' as const, floorRootId: null, holders }));
  const floor: PickRow[] = [];
  let outOfReach = 0;
  for (const { row, holders } of rowsOfTemplate(encounter.encounter_inventories_arr, templateId, read)) {
    const root = holders[0] ?? inventoryIdOf(row);
    if (read.reachable(root) === true) floor.push({ inventoryId: inventoryIdOf(row), qty: qtyOf(row), source: 'floor', floorRootId: root, holders });
    else outOfReach++;
  }
  return { rows: [...carried, ...floor], outOfReach };
}
export const pickCount = (rows: PickRow[]): number => rows.reduce((n, r) => n + r.qty, 0);

/** requires_tool entries → template ids through the shared legacy/structured layer-list reader. */
export function requiredToolIds(entries: unknown[]): string[] {
  const ids: string[] = [];
  for (const entry of entries) {
    const r = asRow(entry);
    const id = upper(layerListEntryUuid(entry) || r?.item_uuid);
    if (isUuid(id)) ids.push(id);
  }
  return [...new Set(ids)];
}
/** lockpicking_caster_required_tools_failure: Lockpicking Tools always, then every other requires_tool on the lock. */
export function toolsGate(actorRow: Row, encounter: Row, lockRequiresTool: unknown[], read: ToolReaders): { failure: string | null; picks: PickRow[] } {
  const picks = usableStacks(actorRow, encounter, LOCKPICK_TOOLS_ITEM_ID, read);
  if (picks.rows.length === 0) return { failure: picks.outOfReach > 0 ? 'lockpicking_tools_out_of_reach' : 'no_lockpicking_tools', picks: [] };
  for (const toolId of requiredToolIds(lockRequiresTool)) {
    if (toolId === LOCKPICK_TOOLS_ITEM_ID) continue;
    if (usableStacks(actorRow, encounter, toolId, read).rows.length === 0) return { failure: 'missing_required_tool', picks: picks.rows };
  }
  return { failure: null, picks: picks.rows };
}
/** lockpick_lock_consumes_picks: the lock's requires_tool lists Lockpicking Tools. */
export const lockConsumesPicks = (lockRequiresTool: unknown[]): boolean => requiredToolIds(lockRequiresTool).includes(LOCKPICK_TOOLS_ITEM_ID);

// ---- the attempt -----------------------------------------------------------------------------------------------------------
/** The actor's live `lockpicking` status bound to this lock (status_origin_id; an unreadable origin is accepted — PHP parity). */
export function activeAttempt(actorRow: Row, lockId: string): Row | null {
  for (const e of statusEntries(actorRow, 'target')) {
    if (statusItemOf(e) !== LOCKPICKING_STATUS_ID) continue;
    const origin = upper(e.status_origin_id ?? asRow(e.status_arr)?.status_origin_id);
    if (origin && lockId && origin !== upper(lockId)) continue;
    return e;
  }
  return null;
}
/** The result verdict: the angle (clamped to the arc, as PHP clamps) inside the frozen effective zone. */
export function pickSucceeds(angleDeg: number, effZone: [number, number]): { angle: number; success: boolean } {
  const angle = clampAngleToArc(angleDeg);
  return { angle, success: angle >= effZone[0] && angle <= effZone[1] };
}
export { lockIsLocked };
